import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Phone, 
  Pencil, 
  Trash2, 
  User,
  Sparkles,
  Volume2,
  ArrowLeft,
  ChevronRight,
  Bot,
  Lock,
  AlertTriangle,
  Crown,
  Copy,
  MoreVertical
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PresetSelector, AGENT_PRESETS, TemplateSwitcher } from "@/components/AgentPresets";

const API = `${process.env.REACT_APP_API_URL}/api`;

const VOICES = [
  { id: "alloy", name: "Alloy", description: "Neutral, balanced" },
  { id: "ash", name: "Ash", description: "Clear, articulate" },
  { id: "coral", name: "Coral", description: "Warm, friendly" },
  { id: "echo", name: "Echo", description: "Smooth, calm" },
  { id: "fable", name: "Fable", description: "Expressive, storytelling" },
  { id: "nova", name: "Nova", description: "Energetic, upbeat" },
  { id: "onyx", name: "Onyx", description: "Deep, authoritative" },
  { id: "sage", name: "Sage", description: "Wise, measured" },
  { id: "shimmer", name: "Shimmer", description: "Bright, cheerful" },
];

const AVATAR_OPTIONS = [
  "https://images.unsplash.com/photo-1657152042392-c1f39e52e7c9?w=200",
  "https://images.unsplash.com/photo-1599132880549-bffe2907f808?w=200",
  "https://images.unsplash.com/photo-1575299833801-85ce40813bac?w=200",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200",
  "https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=200",
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=200",
];

// Map preset voice IDs to our voice options
const mapPresetVoice = (presetVoice) => {
  const voiceMap = {
    "female_professional": "nova",
    "female_warm": "coral",
    "male_professional": "onyx",
    "male_warm": "echo"
  };
  return voiceMap[presetVoice] || "alloy";
};

export default function Agents() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [agentToDelete, setAgentToDelete] = useState(null);
  
  // Usage stats for soft gating
  const [usageStats, setUsageStats] = useState(null);
  const [canCreateAgent, setCanCreateAgent] = useState(true);
  
  // Multi-step agent creation
  const [creationStep, setCreationStep] = useState("preset"); // "preset" | "customize" | "switch-template"
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  const [formData, setFormData] = useState({
    name: "",
    personality: "",
    system_prompt: "",
    voice: "alloy",
    avatar_url: AVATAR_OPTIONS[0]
  });

  useEffect(() => {
    fetchAgents();
    fetchUsageStats();
  }, []);

  const fetchAgents = async () => {
    try {
      const res = await axios.get(`${API}/agents`, { headers: getAuthHeader() });
      setAgents(res.data);
    } catch (error) {
      toast.error("Failed to load agents");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsageStats = async () => {
    try {
      const res = await axios.get(`${API}/usage/stats`, { headers: getAuthHeader() });
      setUsageStats(res.data);
      setCanCreateAgent(res.data.can_create_agent);
    } catch (error) {
      console.error("Failed to load usage stats:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.personality || !formData.system_prompt) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingAgent) {
        await axios.put(`${API}/agents/${editingAgent.id}`, formData, { headers: getAuthHeader() });
        toast.success("Agent updated successfully");
      } else {
        await axios.post(`${API}/agents`, formData, { headers: getAuthHeader() });
        toast.success("Agent created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchAgents();
    } catch (error) {
      toast.error(editingAgent ? "Failed to update agent" : "Failed to create agent");
    }
  };

  const handleEdit = (agent) => {
    setEditingAgent(agent);
    setCreationStep("customize"); // Skip preset selection when editing
    setFormData({
      name: agent.name,
      personality: agent.personality,
      system_prompt: agent.system_prompt,
      voice: agent.voice,
      avatar_url: agent.avatar_url || AVATAR_OPTIONS[0]
    });
    setDialogOpen(true);
  };

  const handleClone = async (agent) => {
    if (!canCreateAgent) {
      toast.error("Agent limit reached. Upgrade your plan to create more agents.");
      return;
    }

    try {
      const clonedAgent = {
        name: `${agent.name} (Copy)`,
        personality: agent.personality,
        system_prompt: agent.system_prompt,
        voice: agent.voice,
        avatar_url: agent.avatar_url || AVATAR_OPTIONS[0]
      };
      
      await axios.post(`${API}/agents`, clonedAgent, { headers: getAuthHeader() });
      toast.success(`Cloned "${agent.name}" successfully`);
      fetchAgents();
      fetchUsageStats(); // Refresh usage after cloning
    } catch (error) {
      toast.error("Failed to clone agent");
    }
  };

  const handleDelete = async () => {
    if (!agentToDelete) return;

    try {
      await axios.delete(`${API}/agents/${agentToDelete.id}`, { headers: getAuthHeader() });
      toast.success("Agent deleted");
      setDeleteDialogOpen(false);
      setAgentToDelete(null);
      fetchAgents();
    } catch (error) {
      toast.error("Failed to delete agent");
    }
  };

  const resetForm = () => {
    setEditingAgent(null);
    setSelectedPreset(null);
    setCreationStep("preset");
    setFormData({
      name: "",
      personality: "",
      system_prompt: "",
      voice: "alloy",
      avatar_url: AVATAR_OPTIONS[0]
    });
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
  };

  const handleUsePreset = () => {
    if (selectedPreset) {
      setFormData({
        name: selectedPreset.name,
        personality: selectedPreset.personality,
        system_prompt: selectedPreset.system_prompt,
        voice: mapPresetVoice(selectedPreset.voice),
        avatar_url: AVATAR_OPTIONS[Math.floor(Math.random() * AVATAR_OPTIONS.length)]
      });
    }
    setCreationStep("customize");
  };

  const handleSkipPreset = () => {
    setSelectedPreset(null);
    setCreationStep("customize");
  };

  const handleBackToPresets = () => {
    setCreationStep("preset");
  };

  // Template switching for existing agents
  const handleSwitchTemplate = () => {
    setCreationStep("switch-template");
  };

  const handleApplyTemplate = (preset) => {
    setFormData({
      ...formData,
      name: preset.name,
      personality: preset.personality,
      system_prompt: preset.system_prompt,
      voice: mapPresetVoice(preset.voice)
    });
    setSelectedPreset(preset);
    setCreationStep("customize");
    toast.success(`Applied "${preset.name}" template`);
  };

  const handleCancelSwitch = () => {
    setCreationStep("customize");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full" data-testid="agents-loading">
        <div className="loading-dots flex gap-1">
          <span className="w-2 h-2 bg-primary rounded-full"></span>
          <span className="w-2 h-2 bg-primary rounded-full"></span>
          <span className="w-2 h-2 bg-primary rounded-full"></span>
        </div>
      </div>
    );
  }

  const agentUsage = usageStats?.usage?.agents;

  return (
    <div className="p-8" data-testid="agents-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">Voice Agents</h1>
          <p className="text-muted-foreground">Create and manage your Speakr AI voice agents</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Usage indicator */}
          {agentUsage && (
            <div className="text-right text-sm">
              <span className="text-muted-foreground">Agents: </span>
              <span className={agentUsage.percentage >= 100 ? "text-destructive font-semibold" : "text-foreground"}>
                {agentUsage.used}/{agentUsage.unlimited ? "∞" : agentUsage.limit}
              </span>
            </div>
          )}
          
          {/* Create Agent Button - with soft gating */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button 
                    className={`rounded-full px-6 ${canCreateAgent ? 'glow-primary' : 'opacity-60 cursor-not-allowed'}`}
                    onClick={canCreateAgent ? openCreateDialog : undefined}
                    disabled={!canCreateAgent}
                    data-testid="create-agent-btn"
                  >
                    {canCreateAgent ? (
                      <Plus className="w-5 h-5 mr-2" />
                    ) : (
                      <Lock className="w-5 h-5 mr-2" />
                    )}
                    Create Agent
                  </Button>
                </span>
              </TooltipTrigger>
              {!canCreateAgent && (
                <TooltipContent side="bottom" className="max-w-xs">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Agent Limit Reached</p>
                      <p className="text-xs text-muted-foreground">
                        You've used all {agentUsage?.limit} agent{agentUsage?.limit !== 1 ? 's' : ''} on your {usageStats?.plan?.name} plan.
                      </p>
                      <Button 
                        size="sm" 
                        variant="link" 
                        className="p-0 h-auto text-xs mt-1"
                        onClick={() => navigate('/dashboard/billing')}
                      >
                        <Crown className="w-3 h-3 mr-1" />
                        Upgrade Plan
                      </Button>
                    </div>
                  </div>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Agent Limit Warning Banner */}
      {!canCreateAgent && (
        <Card className="mb-6 border-amber-500/30 bg-amber-500/5" data-testid="agent-limit-banner">
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="font-medium">Agent Limit Reached</p>
                <p className="text-sm text-muted-foreground">
                  Upgrade your plan to create more AI agents and unlock advanced features.
                </p>
              </div>
            </div>
            <Button onClick={() => navigate('/dashboard/billing')} className="shrink-0">
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="agents-grid">
        {agents.map((agent) => (
          <Card 
            key={agent.id} 
            className="card-hover bg-card border-border group"
            data-testid={`agent-card-${agent.name.toLowerCase()}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start gap-4 mb-4">
                <Avatar className="w-16 h-16 ring-2 ring-primary/20">
                  <AvatarImage src={agent.avatar_url} alt={agent.name} />
                  <AvatarFallback>{agent.name[0]}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-lg truncate">{agent.name}</h3>
                  <p className="text-sm text-muted-foreground truncate">{agent.personality}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge variant="secondary" className="text-xs">
                      <Volume2 className="w-3 h-3 mr-1" />
                      {agent.voice}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {agent.total_calls} calls
                    </Badge>
                  </div>
                </div>
              </div>

              <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                {agent.system_prompt}
              </p>

              <div className="flex items-center gap-2">
                <Button 
                  className="flex-1 rounded-full"
                  onClick={() => navigate(`/dashboard/call/${agent.id}`)}
                  data-testid={`call-${agent.name.toLowerCase()}-btn`}
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call
                </Button>
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="rounded-full"
                  onClick={() => handleEdit(agent)}
                  data-testid={`edit-${agent.name.toLowerCase()}-btn`}
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                
                {/* More Actions Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="rounded-full"
                      data-testid={`more-${agent.name.toLowerCase()}-btn`}
                    >
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem 
                      onClick={() => handleClone(agent)}
                      disabled={!canCreateAgent}
                      data-testid={`clone-${agent.name.toLowerCase()}-btn`}
                    >
                      <Copy className="w-4 h-4 mr-2" />
                      Clone Agent
                      {!canCreateAgent && (
                        <Badge variant="outline" className="ml-2 text-xs">
                          Limit
                        </Badge>
                      )}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="text-destructive focus:text-destructive"
                      onClick={() => {
                        setAgentToDelete(agent);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-${agent.name.toLowerCase()}-btn`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Agent
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Empty State */}
        {agents.length === 0 && (
          <Card className="col-span-full bg-card/50 border-dashed border-2" data-testid="no-agents">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Sparkles className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-heading font-semibold mb-2">No agents yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Create your first AI voice agent to get started
              </p>
              <Button onClick={openCreateDialog} data-testid="create-first-agent-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Agent
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create/Edit Dialog - Multi-Step */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent 
          className={(creationStep === "preset" || creationStep === "switch-template") ? "sm:max-w-[900px]" : "sm:max-w-[500px]"} 
          data-testid="agent-dialog"
        >
          {/* Step 1: Preset Selection (only for new agents) */}
          {creationStep === "preset" && !editingAgent && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">
                  Create New Agent
                </DialogTitle>
                <DialogDescription>
                  Start with a template or build from scratch
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4">
                <PresetSelector
                  selectedPreset={selectedPreset}
                  onSelectPreset={handlePresetSelect}
                  onSkip={handleSkipPreset}
                />
              </div>

              <DialogFooter className="flex items-center justify-between border-t pt-4">
                <Button variant="ghost" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleUsePreset}
                  disabled={!selectedPreset}
                  data-testid="use-preset-btn"
                >
                  Use Template
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </DialogFooter>
            </>
          )}

          {/* Template Switcher (for existing agents) */}
          {creationStep === "switch-template" && editingAgent && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleCancelSwitch}
                    className="mr-2"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </Button>
                  Switch Template
                </DialogTitle>
                <DialogDescription>
                  Choose a new template for "{editingAgent.name}"
                </DialogDescription>
              </DialogHeader>
              
              <div className="py-4">
                <TemplateSwitcher
                  currentTemplate={selectedPreset}
                  onSelectTemplate={handleApplyTemplate}
                  onCancel={handleCancelSwitch}
                />
              </div>
            </>
          )}

          {/* Step 2: Customize Agent */}
          {creationStep === "customize" && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  {!editingAgent && selectedPreset && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleBackToPresets}
                      className="mr-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                    </Button>
                  )}
                  {editingAgent ? "Edit Agent" : selectedPreset ? `Customize ${selectedPreset.name}` : "Create Custom Agent"}
                </DialogTitle>
                <DialogDescription>
                  {selectedPreset 
                    ? "Customize the template to match your needs" 
                    : "Configure your AI voice agent's personality and behavior"
                  }
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Show preset badge if using a preset */}
                {!editingAgent && selectedPreset && (
                  <div className="mb-4">
                    <Badge variant="secondary" className="mb-2">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Using {selectedPreset.name} template
                    </Badge>
                  </div>
                )}

                {/* Change Template button for editing */}
                {editingAgent && (
                  <div className="mb-4 p-3 bg-muted/50 rounded-lg flex items-center justify-between">
                    <div className="text-sm">
                      <span className="text-muted-foreground">Want a fresh start?</span>
                    </div>
                    <Button 
                      type="button"
                      variant="outline" 
                      size="sm"
                      onClick={handleSwitchTemplate}
                      data-testid="change-template-btn"
                    >
                      <Sparkles className="w-4 h-4 mr-1" />
                      Apply Template
                    </Button>
                  </div>
                )}

                {/* Avatar Selection */}
                <div className="space-y-2">
                  <Label>Avatar</Label>
                  <div className="flex gap-2 flex-wrap">
                    {AVATAR_OPTIONS.map((url, index) => (
                      <button
                        key={index}
                        type="button"
                        className={`rounded-full p-1 transition-all ${
                          formData.avatar_url === url 
                            ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' 
                            : 'hover:ring-2 hover:ring-muted'
                        }`}
                        onClick={() => setFormData({ ...formData, avatar_url: url })}
                        data-testid={`avatar-option-${index}`}
                      >
                        <Avatar className="w-12 h-12">
                          <AvatarImage src={url} />
                          <AvatarFallback><User className="w-6 h-6" /></AvatarFallback>
                        </Avatar>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Luna"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="agent-name-input"
                  />
                </div>

                {/* Personality */}
                <div className="space-y-2">
                  <Label htmlFor="personality">Personality *</Label>
                  <Input
                    id="personality"
                    placeholder="e.g., Friendly customer support agent"
                    value={formData.personality}
                    onChange={(e) => setFormData({ ...formData, personality: e.target.value })}
                    data-testid="agent-personality-input"
                  />
                </div>

                {/* Voice */}
                <div className="space-y-2">
                  <Label>Voice</Label>
                  <Select 
                    value={formData.voice} 
                    onValueChange={(val) => setFormData({ ...formData, voice: val })}
                  >
                    <SelectTrigger data-testid="agent-voice-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {VOICES.map((voice) => (
                        <SelectItem key={voice.id} value={voice.id} data-testid={`voice-option-${voice.id}`}>
                          <div className="flex items-center gap-2">
                            <span>{voice.name}</span>
                            <span className="text-xs text-muted-foreground">- {voice.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* System Prompt */}
                <div className="space-y-2">
                  <Label htmlFor="system_prompt">System Prompt *</Label>
                  <Textarea
                    id="system_prompt"
                    placeholder="Describe how the agent should behave..."
                    value={formData.system_prompt}
                    onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                    rows={4}
                    data-testid="agent-prompt-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    This defines the agent's behavior and knowledge
                  </p>
                </div>

                <DialogFooter>
                  <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-agent-btn">
                    {editingAgent ? "Save Changes" : "Create Agent"}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Agent</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{agentToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
